import React from 'react';
import {
  tokensToCssVarObject,
  DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT,
  DEFAULT_SIDEBAR_LOGO_MAX_WIDTH,
  DEFAULT_SIDEBAR_LOGO_ALIGN,
  DEFAULT_LOGO_CORNER_RADIUS,
  LOGO_CORNER_RADIUS_MAX,
  sidebarLogoAlignToFlexJustify,
} from '../utils/branding';
import BrandedSidebarLogo from './BrandedSidebarLogo';
import { DEFAULT_DASHBOARD_PANELS } from '../services/userPreferencesService';

/** WhitePanel title: matches ProjectDashboard (snag → “Snags & variations”). */
function panelTitleForBrandingPreview(panel) {
  if (panel.key === 'snag') return 'Snags & variations';
  return panel.title;
}

/**
 * Mini mock content styled like the real *Preview components (branding tokens, not hard-coded blues).
 * Accent appears where the real previews do: e.g. play (activity), DOC programme chip, sign-off bar (documents).
 */
function BrandingPanelSampleContent({ panelKey, compact = true }) {
  const t = (size, weight, color) => ({
    fontSize: compact ? size - 1 : size,
    fontWeight: weight,
    color: color || 'var(--branding-app-surface-panel-fg)',
    lineHeight: 1.25,
  });

  if (panelKey === 'activity') {
    return (
      <div style={{ display: 'flex', gap: 10, marginTop: 8, minHeight: 0, flex: 1, alignItems: 'flex-start' }}>
        <div
          style={{
            width: compact ? 52 : 64,
            height: compact ? 52 : 64,
            borderRadius: 8,
            background: 'linear-gradient(135deg, var(--branding-app-surface) 0%, var(--branding-app-surface-panel-border) 100%)',
            border: '1px solid var(--branding-app-surface)',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...t(12, 600) }}>Concrete</div>
          <div
            style={{
              ...t(11, 400, 'var(--branding-app-surface-panel-sub)'),
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Lift pour complete — level 3
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 6,
              fontSize: 10,
              color: 'var(--branding-app-surface-panel-hint)',
            }}
          >
            <span>18m ago</span>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                lineHeight: 1,
                flexShrink: 0,
              }}
              title="Play voice note (preview)"
            >
              ▶
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (panelKey === 'attendance') {
    return (
      <div style={{ marginTop: 6, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div>
          <div style={{ ...t(12, 600) }}>7 On-Site</div>
          <div style={{ ...t(10, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>3 groups active</div>
        </div>
        <div
          style={{
            background: 'var(--branding-app-surface)',
            borderRadius: 6,
            padding: 6,
            border: '1px solid var(--branding-app-surface-panel-border)',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: 'var(--branding-app-surface-panel-muted)',
              marginBottom: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
            Concrete (4)
          </div>
          <div style={{ fontSize: 10, color: 'var(--branding-app-surface-panel-fg)', paddingLeft: 8, lineHeight: 1.5 }}>
            Alex K{' '}
            <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontWeight: 400 }}>(13:20)</span>
            , Sam R{' '}
            <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontWeight: 400 }}>(12:45)</span>
            <span style={{ color: 'var(--branding-app-surface-panel-hint)' }}> +2 others</span>
          </div>
        </div>
        <div
          style={{
            background: 'var(--branding-app-surface)',
            borderRadius: 6,
            padding: 6,
            border: '1px solid var(--branding-app-surface-panel-border)',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: 'var(--branding-app-surface-panel-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
            Electrical (2)
          </div>
        </div>
      </div>
    );
  }

  if (panelKey === 'gantt') {
    return (
      <div style={{ marginTop: 6, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <div
            style={{
              width: 24,
              height: 24,
              background: 'var(--branding-app-primary-soft)',
              border: '1px solid var(--branding-app-primary)',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 7,
              fontWeight: 700,
              color: 'var(--branding-app-primary)',
              flexShrink: 0,
            }}
          >
            DOC
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Programme v4.xlsx</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>All teams</div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 2 }}>2d ago</div>
          </div>
        </div>
        <div
          style={{
            borderTop: '1px solid var(--branding-app-surface)',
            paddingTop: 6,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 6.5,
              fontWeight: 700,
              color: '#0ea5e9',
              flexShrink: 0,
            }}
          >
            TASK
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600) }}>First fix — Block B</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>Concrete</div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 2 }}>12 Mar – 20 Mar</div>
          </div>
        </div>
      </div>
    );
  }

  if (panelKey === 'design') {
    return (
      <div style={{ marginTop: 6, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <div
            style={{
              width: 22,
              height: 22,
              background: 'var(--branding-app-primary-soft)',
              border: '1px solid var(--branding-app-primary)',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 7,
              fontWeight: 600,
              color: 'var(--branding-app-primary)',
              flexShrink: 0,
            }}
          >
            DOC
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>RFI-204 responses.docx</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>Awaiting sign-off</div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 1 }}>4h ago</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <div
            style={{
              width: 22,
              height: 22,
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 7,
              fontWeight: 600,
              color: '#d97706',
              flexShrink: 0,
            }}
          >
            DWG
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600) }}>L3 ceiling coordination.dwg</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>Revision C issued</div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 1 }}>1d ago</div>
          </div>
        </div>
      </div>
    );
  }

  if (panelKey === 'documents') {
    return (
      <div style={{ marginTop: 6, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <div
            style={{
              width: 24,
              height: 24,
              background: '#f3e8ff',
              border: '1px solid #c4b5fd',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 7,
              fontWeight: 700,
              color: '#7c3aed',
              flexShrink: 0,
            }}
          >
            DOC
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600) }}>Subcontractor induction pack</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>In progress</div>
            <div style={{ marginTop: 5 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 2,
                }}
              >
                <span style={{ fontSize: 8, color: 'var(--branding-app-surface-panel-muted)' }}>3 of 8 signed</span>
                <span style={{ fontSize: 8, color: 'var(--branding-app-surface-panel-muted)' }}>38%</span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: 3,
                  background: 'var(--branding-app-surface-panel-border)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: '38%',
                    height: '100%',
                    background: 'var(--branding-app-primary)',
                    borderRadius: 2,
                  }}
                />
              </div>
            </div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 3 }}>6h ago</div>
          </div>
        </div>
      </div>
    );
  }

  if (panelKey === 'snag') {
    return (
      <div style={{ marginTop: 6, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <div
            style={{
              width: 24,
              height: 24,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 6.5,
              fontWeight: 700,
              color: '#dc2626',
              flexShrink: 0,
            }}
          >
            SNAG
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600) }}>Fire stopping — riser 2B</div>
            <div style={{ ...t(9, 400, 'var(--branding-app-surface-panel-sub)'), marginTop: 1 }}>In progress • Electrical</div>
            <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 7,
                  padding: '1px 5px',
                  borderRadius: 6,
                  background: '#f97316',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                high
              </span>
              <span
                style={{
                  fontSize: 7,
                  padding: '1px 5px',
                  borderRadius: 6,
                  background: 'var(--branding-app-surface)',
                  color: 'var(--branding-app-surface-panel-muted)',
                  fontWeight: 500,
                }}
              >
                quality
              </span>
            </div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)', marginTop: 2 }}>Yesterday</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 5, paddingTop: 4, borderTop: '1px solid var(--branding-app-surface)' }}>
          <div
            style={{
              width: 24,
              height: 24,
              background: '#f3e8ff',
              border: '1px solid #c4b5fd',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 6.5,
              fontWeight: 700,
              color: '#7c3aed',
              flexShrink: 0,
            }}
          >
            VAR
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...t(11, 600) }}>SI-14 ceiling void depth</div>
            <div style={{ fontSize: 9, color: 'var(--branding-app-surface-panel-hint)' }}>Pending • Concrete</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Local preview — sets branding CSS variables on a wrapper (does not touch :root).
 * @param {{ tokens: object; prominent?: boolean; logoUrl?: string | null; logoMaxHeight?: number; logoMaxWidth?: number; logoAlign?: 'start' | 'center' | 'end'; logoCornerRadius?: number; logoBacking?: 'none' | 'white' | 'black' }} props
 */
export default function BrandingSandbox({
  tokens,
  prominent = false,
  logoUrl = null,
  logoMaxHeight,
  logoMaxWidth,
  logoAlign = DEFAULT_SIDEBAR_LOGO_ALIGN,
  logoCornerRadius = DEFAULT_LOGO_CORNER_RADIUS,
  logoBacking = 'none',
}) {
  const mh = typeof logoMaxHeight === 'number' && !Number.isNaN(logoMaxHeight) ? logoMaxHeight : DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT;
  const mw = typeof logoMaxWidth === 'number' && !Number.isNaN(logoMaxWidth) ? logoMaxWidth : DEFAULT_SIDEBAR_LOGO_MAX_WIDTH;
  const align =
    logoAlign === 'start' || logoAlign === 'center' || logoAlign === 'end' ? logoAlign : DEFAULT_SIDEBAR_LOGO_ALIGN;
  const logoJustify = sidebarLogoAlignToFlexJustify(align);
  const rawBacking = logoBacking === 'panel' ? 'white' : logoBacking;
  const backing =
    rawBacking === 'white' || rawBacking === 'black' ? rawBacking : 'none';
  const cornerR =
    typeof logoCornerRadius === 'number' && !Number.isNaN(logoCornerRadius)
      ? Math.min(LOGO_CORNER_RADIUS_MAX, Math.max(0, Math.round(logoCornerRadius)))
      : DEFAULT_LOGO_CORNER_RADIUS;
  const vars = tokensToCssVarObject(tokens);

  const sidebarW = prominent ? 228 : 200;
  const btnPad = prominent ? '8px 12px' : '8px 10px';
  const mainPad = prominent ? '14px 16px 18px' : '12px 14px';
  const titleSize = prominent ? 15 : 13;
  const logoBoxMinH = prominent ? Math.min(mh + 16, 96) : Math.min(mh + 12, 72);
  const shellMinH = prominent ? 440 : 120;

  const navItem = (label, active) => ({
    fontSize: prominent ? 13 : 12,
    fontWeight: 500,
    color: 'var(--branding-app-surface-panel-fg)',
    padding: '8px 10px',
    borderRadius: 8,
    backgroundColor: active ? 'var(--branding-app-surface)' : 'transparent',
  });

  const mainColumn = (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: mainPad,
        background: 'var(--branding-app-surface)',
        overflow: 'auto',
        maxHeight: prominent ? 560 : undefined,
      }}
    >
      {prominent ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10,
              marginBottom: 20,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0, flex: '1 1 140px' }}>
              <h2
                style={{
                  fontSize: titleSize,
                  fontWeight: 600,
                  color: 'var(--branding-app-surface-panel-fg)',
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                Sample project
              </h2>
            </div>
            <div
              style={{
                flexShrink: 0,
                background: 'var(--branding-app-surface-panel)',
                borderRadius: 12,
                padding: prominent ? '14px 16px' : '12px 14px',
                minWidth: 'clamp(160px, 28%, 240px)',
                maxWidth: 280,
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                cursor: 'default',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: 'var(--branding-app-surface-panel-fg)',
                    fontSize: prominent ? 14 : 12,
                  }}
                >
                  Teams
                </div>
                <button
                  type="button"
                  style={{
                    marginLeft: 'auto',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    border: 'none',
                    borderRadius: 8,
                    padding: prominent ? '6px 14px' : '5px 12px',
                    fontWeight: 500,
                    cursor: 'default',
                    fontSize: prominent ? 13 : 12,
                    lineHeight: 1,
                  }}
                  title="Add Team"
                  aria-label="Add Team"
                >
                  +
                </button>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#3b82f6',
                      marginRight: 8,
                      border: '1px solid var(--branding-app-surface-panel-border)',
                    }}
                  />
                  <span style={{ fontWeight: 500, color: 'var(--branding-app-surface-panel-fg)', fontSize: prominent ? 13 : 12 }}>Concrete</span>
                  <span style={{ color: 'var(--branding-app-surface-panel-muted)', marginLeft: 'auto', fontSize: 11 }}>Sub</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#22c55e',
                      marginRight: 8,
                      border: '1px solid var(--branding-app-surface-panel-border)',
                    }}
                  />
                  <span style={{ fontWeight: 500, color: 'var(--branding-app-surface-panel-fg)', fontSize: prominent ? 13 : 12 }}>Electrical</span>
                  <span style={{ color: 'var(--branding-app-surface-panel-muted)', marginLeft: 'auto', fontSize: 11 }}>Sub</span>
                </li>
              </ul>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gridTemplateRows: 'repeat(2, minmax(0, auto))',
              gap: 24,
              marginTop: 8,
            }}
          >
            {DEFAULT_DASHBOARD_PANELS.map((panel) => (
              <div
                key={panel.key}
                style={{
                  background: 'var(--branding-app-surface-panel)',
                  color: 'var(--branding-app-surface-panel-fg)',
                  borderRadius: 16,
                  padding: '20px 22px',
                  boxSizing: 'border-box',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  /* Scaled from WhitePanel: height clamp(320px, 28vh, 520px) */
                  height: 'clamp(168px, 22vh, 240px)',
                  minHeight: 168,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'default',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: prominent ? 15 : 14,
                    fontWeight: 600,
                    color: 'var(--branding-app-surface-panel-fg)',
                    lineHeight: 1.25,
                    flexShrink: 0,
                  }}
                >
                  {panelTitleForBrandingPreview(panel)}
                </h3>
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                  }}
                >
                  <BrandingPanelSampleContent panelKey={panel.key} compact={!prominent} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--branding-app-surface-panel-muted)', lineHeight: 1.5 }}>
          Accent colour on links and buttons matches your settings.
        </p>
      )}
    </div>
  );

  return (
    <div
      style={{
        ...vars,
        borderRadius: prominent ? 14 : 12,
        overflow: 'hidden',
        border: '1px solid var(--branding-app-surface-panel-border)',
        boxShadow: prominent ? '0 8px 30px rgba(15,23,42,0.08)' : '0 4px 20px rgba(0,0,0,0.06)',
        minHeight: prominent ? 420 : undefined,
      }}
    >
      <div style={{ display: 'flex', minHeight: shellMinH, alignItems: 'stretch' }}>
        <div
          style={{
            width: sidebarW,
            flexShrink: 0,
            background: 'var(--branding-sidebar-bg)',
            borderRight: '2px solid var(--branding-sidebar-border)',
            padding: prominent ? '12px 12px 14px' : '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: prominent ? 10 : 10,
          }}
        >
          <div
            style={{
              minHeight: logoBoxMinH,
              borderRadius: 8,
              background: logoUrl ? 'var(--branding-app-surface-panel)' : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
              border: logoUrl ? '1px solid var(--branding-app-surface-panel-border)' : '1px dashed var(--branding-app-surface-panel-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: logoJustify,
              padding: 8,
              overflow: 'hidden',
            }}
          >
            {logoUrl ? (
              <BrandedSidebarLogo
                src={logoUrl}
                alt="Company logo preview"
                isCustomCompanyLogo
                maxWidth={Math.min(mw, sidebarW - 24)}
                maxHeight={mh}
                cornerRadius={cornerR}
                backing={backing}
              />
            ) : (
              <span style={{ fontSize: prominent ? 11 : 10, fontWeight: 600, color: 'var(--branding-app-surface-panel-muted)', textAlign: 'center' }}>
                Your logo
              </span>
            )}
          </div>

          <div style={navItem('Home', true)}>Home</div>

          <button
            type="button"
            style={{
              padding: btnPad,
              borderRadius: 8,
              border: 'none',
              background: 'var(--branding-app-primary)',
              color: 'var(--branding-app-primary-fg)',
              fontSize: prominent ? 12 : 11,
              fontWeight: 600,
              cursor: 'default',
            }}
          >
            New project
          </button>

          <div style={navItem('Your projects', false)}>Your projects</div>

          <div style={{ flex: 1, minHeight: 8 }} aria-hidden />
        </div>
        {mainColumn}
      </div>
    </div>
  );
}
